import CreativeEngine from "@cesdk/engine";
import type {DesignBlockId} from "@cesdk/cesdk-js";
import type {SlideInfo} from "../api/presentations.ts";


/**
 * Transforms a number in the range of -100 to 100, to -1 to 1.
 * @param v
 */
const toImglyBrightness = (v: number) =>
    // -100..100  →  -1..1  (0 -> 0)
    Math.max(-1, Math.min(1, Number(v) / 100));

const fromImglyBrightness = (v: number) =>
    Math.max(-100, Math.min(100, Number(v) * 100));

const ADJUSTMENTS_BRIGHTNESS_PATH = 'effect/adjustments/brightness';
const adjustmentsEffectIds = new Map<number, number>();

export function getAdjustmentsEffectId(
    engine: any,
    blockId: number,
    createIfMissing = true
): number | null {
    if (!engine?.block?.supportsEffects(blockId)) {
        return null;
    }

    const effects: number[] = engine.block.getEffects(blockId) ?? [];
    const cachedId = adjustmentsEffectIds.get(blockId);
    if (cachedId != null && effects.includes(cachedId)) {
        return cachedId;
    }
    adjustmentsEffectIds.delete(blockId);

    for (const effectId of effects) {
        if (effectSupportsBrightness(engine, effectId)) {
            adjustmentsEffectIds.set(blockId, effectId);
            return effectId;
        }
    }

    if (!createIfMissing) {
        return null;
    }

    try {
        const created = engine.block.createEffect('adjustments');
        if (created == null) {
            return null;
        }
        engine.block.appendEffect(blockId, created);
        adjustmentsEffectIds.set(blockId, created);
        return created;
    } catch {
        return null;
    }
}

export function effectSupportsBrightness(engine: any, effectId: number): boolean {
    try {
        engine.block.getFloat(effectId, ADJUSTMENTS_BRIGHTNESS_PATH);
        return true;
    } catch {
        return false;
    }
}

/**
 *
 * @param engine
 * @param blockId
 * @param builderValue
 */
export function setBlockBrightness(engine: any, blockId: number, builderValue: number) {
    if (!engine?.block?.supportsEffects?.(blockId)) return;
    const adjustments = getAdjustmentsEffectId(engine, blockId, true);
    if (adjustments == null) return;

    const b = toImglyBrightness(builderValue);
    engine.block.setFloat(adjustments, ADJUSTMENTS_BRIGHTNESS_PATH, b);
}

/**
 * Get the current brightness value from a block
 * @param engine
 * @param blockId
 * @returns The brightness value in builder scale (-100 to 100), or 0 if no brightness is set
 */
export function getBlockBrightness(engine: any, blockId: number): number {
    if (!engine?.block?.supportsEffects?.(blockId)) return 0;

    const adjustments = getAdjustmentsEffectId(engine, blockId, false);
    if (adjustments == null) {
        return 0; // Default brightness
    }

    try {
        const imglyBrightness = engine.block.getFloat(adjustments, ADJUSTMENTS_BRIGHTNESS_PATH);
        return fromImglyBrightness(imglyBrightness);
    } catch {
        return 0;
    }
}

/**
 * Sort slides by slideIndex to ensure correct order
 */
export const sortSlides = (slidesForSession: SlideInfo[]): SlideInfo[] => {
    return [...slidesForSession].sort((a, b) => {
            const indexA = a.slideIndex ?? 0;
            const indexB = b.slideIndex ?? 0;
            return indexA - indexB;
    });
}

/**
 * Sort slides by slideIndex and re-index.
 */
export const reindexSlides = (slidesForSession: SlideInfo[]): SlideInfo[] => {
    // Sort by original slideIndex asc, then ID asc
    const sorted = [...slidesForSession].sort((a, b) => {
        const indexA = a.slideIndex ?? 0;
        const indexB = b.slideIndex ?? 0;
        if (indexA !== indexB) {
            return indexA - indexB;
        }
        return (a.id ?? 0) - (b.id ?? 0);
    });

    // Re-index from 0 and warn if changes occurred
    const reindexedSlides = sorted.map((slide, newIndex) => {
        if (slide.slideIndex !== newIndex) {
            console.warn(`[Slide Re-index] Slide ID ${slide.id} ("${slide.name || 'unnamed'}") index changed from ${slide.slideIndex} to ${newIndex}.`);
        }
        return {
            ...slide,
            slideIndex: newIndex
        };
    });

    return reindexedSlides;
}

export const getBlocksRecursive = function(engine: CreativeEngine, parentBlockId: DesignBlockId, result: DesignBlockId[] = []) {
    const children: DesignBlockId[] = engine.block.getChildren(parentBlockId) || [];

    for (const childId of children) {
        result.push(childId);
        getBlocksRecursive(engine, childId, result);
    }

    return result;
}


/**
 * Get the page that a block belongs to.
 * @param engine
 * @param id
 */
export function findOwningPage(engine: CreativeEngine, id: number): number | null {
    let cur: number | null = id;
    while (cur && engine.block.isValid?.(cur)) {
        if (engine.block.getType(cur) === '//ly.img.ubq/page') return cur;
        const p = engine.block.getParent?.(cur);
        if (p == null || p === 0) break;
        cur = p;
    }
    return null;
}


export function getPageVideoBlocks(engine: CreativeEngine, pageId: DesignBlockId, supportedVideoFormats: string[]): DesignBlockId[]
{
    const blocks: DesignBlockId[] = [];
    const stack: DesignBlockId[] = [pageId];

    while (stack.length) {
        const id: DesignBlockId = stack.pop()!;

        const children = engine.block.getChildren(id);
        for (let i = 0; i < children.length; i++)
            stack.push(children[i]);

        if (!id) {
            console.warn("Empty ID {}", id);
            continue;
        }

        if (!engine.block.supportsFill(id))
            continue;

        // If this block has a fill and that fill is a video fill => treat as video block
        const fillId = engine.block.getFill(id);
        if (fillId) {
            // check fill type (fast)
            //const fillType = engine.block.getType(fillId);
            //if (fillType === "//ly.img.ubq/fill/video") {
            //    blocks.push(id);
            //    continue;
            //}

            if (!engine.block.isValid(fillId)) {
                console.warn("Invalid ID {}", id);
                continue;
            }

            // check for the video fill property (most compatible), and ensure it's a supported format (ie. not gif)
            const props = engine.block.findAllProperties(fillId);
            if (props.includes("fill/video/fileURI"))
            {
                const url: string = engine.block.getString(fillId, "fill/video/fileURI");
                if (url && supportedVideoFormats.some(ext => url.toLowerCase().endsWith(ext))) {
                    blocks.push(id);
                    continue;
                }
            }
        }
    }

    return blocks;
}

export function removeVideoFill(engine: CreativeEngine, blockId: DesignBlockId)
{
    const oldFillId = engine.block.getFill(blockId);

    if (oldFillId
        && engine.block.isValid(oldFillId)
        //&& engine.block.getType(oldFillId) === "//ly.img.ubq/fill/video"
    )
        engine.block.destroy(oldFillId);
}


export function swapVideoFillWithImage(engine: CreativeEngine, blockId: DesignBlockId, imageUri: string)
{
    // Get current fill (likely a video fill)
    const oldFillId = engine.block.getFill(blockId);

    // Create a new image fill and set its URI
    const imageFillId = engine.block.createFill("//ly.img.ubq/fill/image");
    engine.block.setString(imageFillId, "fill/image/imageFileURI", imageUri);

    // Apply new fill to the same block
    engine.block.setFill(blockId, imageFillId);

    // Delete the old fill to avoid dangling blocks
    if (oldFillId)
        engine.block.destroy(oldFillId);
}

export function swapVideoFillUri(engine: CreativeEngine, blockId: DesignBlockId, newVideoUri: string)
{
    // Get the existing fill (this is already a video fill)
    const fillId: DesignBlockId = engine.block.getFill(blockId);
    if (!fillId)
        return;

    // Optional safety: ensure it's actually a video fill
    const fillType = engine.block.getType(fillId);
    if (fillType !== "//ly.img.ubq/fill/video")
        return;

    // Swap only the file URI
    engine.block.setString(fillId, "fill/video/fileURI", newVideoUri);
}

export function getAbsoluteStartOnPage(engine: any, blockId: DesignBlockId, pageId: DesignBlockId): number {
    let t = 0;
    let cur: DesignBlockId | null = blockId;

    while (cur != null && cur !== pageId) {
        if (engine.block.supportsTimeOffset(cur)) {
            t += engine.block.getTimeOffset(cur);
        }
        cur = engine.block.getParent(cur);
    }
    return t;
}

export function getDuration(engine: any, blockId: DesignBlockId): number {
    return engine.block.supportsDuration(blockId) ? engine.block.getDuration(blockId) : 0;
}

export function isVideoBlockMuted(engine: CreativeEngine, blockId: DesignBlockId): boolean {
    const fillId = engine.block.getFill(blockId);
    if (!fillId) return false;

    return engine.block.isMuted(fillId);
}

export function isVideoBlockHasSound(engine: CreativeEngine, blockId: DesignBlockId): boolean {
    const fillId = engine.block.getFill(blockId);
    if (!fillId) return false;

    return !engine.block.isMuted(fillId) && engine.block.getVolume(fillId) !== 0;
}

export function getVisualPageBlocks(engine: CreativeEngine, pageId: DesignBlockId): DesignBlockId[] {
    const all: DesignBlockId[] = getBlocksRecursive(engine, pageId);
    return all.filter(blockId => isBlockVisualBlock(engine, blockId));
}

function isBlockVisualBlock(engine: CreativeEngine, blockId: DesignBlockId): boolean {
    const type = engine.block.getType(blockId);
    return isVisualBlock(type);
}

export function isVisualBlock(type: string): boolean
{
    return (
        type.startsWith('//ly.img.ubq/graphic') ||
        type.startsWith('//ly.img.ubq/image')  ||
        type.startsWith('//ly.img.ubq/text')   ||
        type.startsWith('//ly.img.ubq/shape')  ||
        type.startsWith('//ly.img.ubq/video')  ||
        type.startsWith('//ly.img.ubq/sticker')
    );
}

export function removeVideoBlocks(engine: CreativeEngine, pageId: DesignBlockId)
{
    const IGNORE_VIDEO_FORMATS = ["gif"];
    const blocks: DesignBlockId[] = getPageVideoBlocks(engine, pageId, IGNORE_VIDEO_FORMATS);

    try {
        for (const id of blocks)
            if (engine.block.isValid(id))
                engine.block.destroy(id);
    }
    catch (e) {
        console.error("Error removing video blocks: ", e);
    }
}